// Règlement groupé : un virement (ou chèque) du client solde plusieurs factures d'un coup (montants TTC).
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { AutoFixHighOutlined, SearchOutlined } from '@mui/icons-material';
import { marcheAPI } from '../../services/api';
import { apiError, dateFr, fcfa, PAYMENT_MODES, today } from '../../utils/format';
import { tokens } from '../../theme';

const entier = (v) => { const n = Number(String(v ?? '').replace(/[\s  ]/g, '')); return Number.isFinite(n) ? Math.round(n) : NaN; };
const cle = (f) => `${f.source}-${f.id}`;

export function ReglementDialog({ open, onClose, impayes, onSaved }) {
  const clients = useMemo(() => {
    const m = new Map();
    for (const f of impayes) m.set(f.client_id, { id: f.client_id, nom: f.client_nom, n: (m.get(f.client_id)?.n || 0) + 1 });
    return [...m.values()].sort((a, b) => b.n - a.n);
  }, [impayes]);
  const [f, setF] = useState({ date_reglement: today(), mode: 'VIREMENT', reference: '', montant_recu: '', note: '', client_id: '' });
  const [sel, setSel] = useState({}); // clé -> montant (texte)
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF({ date_reglement: today(), mode: 'VIREMENT', reference: '', montant_recu: '', note: '', client_id: clients[0]?.id || '' });
    setSel({}); setQ(''); setError('');
  }, [open]); // eslint-disable-line

  const liste = useMemo(() => impayes
    .filter((x) => x.client_id === f.client_id)
    .sort((a, b) => String(a.echeance || a.validation_compta || a.date_enlevement || '9').localeCompare(String(b.echeance || b.validation_compta || b.date_enlevement || '9'))),
  [impayes, f.client_id]);
  const visibles = liste.filter((x) => !q || `${x.numero} ${x.marche_numero}`.toLowerCase().includes(q.toLowerCase()));
  const montantDe = (x) => (x.source === 'ERP' ? entier(sel[cle(x)]) : x.reste_ttc);
  const coches = liste.filter((x) => sel[cle(x)] !== undefined);
  const affecte = coches.reduce((s, x) => s + (Number.isFinite(montantDe(x)) ? montantDe(x) : 0), 0);
  const recu = f.montant_recu === '' ? null : entier(f.montant_recu);
  const ecart = recu === null || !Number.isFinite(recu) ? null : recu - affecte;
  const invalides = coches.filter((x) => x.source === 'ERP' && (!(montantDe(x) > 0) || montantDe(x) > x.reste_ttc));

  const basculer = (x) => setSel((s) => {
    const n = { ...s };
    if (n[cle(x)] !== undefined) delete n[cle(x)]; else n[cle(x)] = String(x.reste_ttc);
    return n;
  });
  // Répartit le montant reçu sur les factures les plus anciennes (la dernière peut être réglée en partie)
  const repartir = () => {
    if (!(recu > 0)) return;
    let reste = recu;
    const n = {};
    for (const x of liste) {
      if (x.reste_ttc <= 0 || reste <= 0) continue;
      if (x.source === 'REPRISE') { if (x.reste_ttc <= reste) { n[cle(x)] = String(x.reste_ttc); reste -= x.reste_ttc; } continue; }
      const m = Math.min(reste, x.reste_ttc); n[cle(x)] = String(m); reste -= m;
    }
    setSel(n);
  };

  const enregistrer = async () => {
    if (ecart && !window.confirm(`Le total réglé (${fcfa(affecte)} F) diffère du montant reçu (${fcfa(recu)} F) : écart de ${fcfa(ecart)} F. Enregistrer quand même ?`)) return;
    setBusy(true); setError('');
    try {
      const r = await marcheAPI.reglement({
        ...f, montant_recu: f.montant_recu === '' ? null : recu,
        lignes: coches.map((x) => ({ source: x.source, id: x.id, montant: x.source === 'ERP' ? montantDe(x) : undefined })),
      });
      onSaved(r.data);
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Enregistrer un règlement</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {clients.length > 1 && (
            <Grid item xs={12} md={3}>
              <TextField select fullWidth size="small" label="Client" value={f.client_id} onChange={(e) => { setF({ ...f, client_id: e.target.value }); setSel({}); }}>
                {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.nom}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          <Grid item xs={6} md={clients.length > 1 ? 2 : 2.5}>
            <TextField fullWidth size="small" type="date" label="Date du règlement" InputLabelProps={{ shrink: true }} inputProps={{ max: today() }}
              value={f.date_reglement} onChange={(e) => setF({ ...f, date_reglement: e.target.value })} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Mode" value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}>
              {Object.entries(PAYMENT_MODES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={clients.length > 1 ? 2.5 : 3.5}>
            <TextField fullWidth size="small" label="Référence" placeholder="N° de virement, d'avis de paiement…" value={f.reference}
              onChange={(e) => setF({ ...f, reference: e.target.value })} inputProps={{ maxLength: 100 }} />
          </Grid>
          <Grid item xs={12} sm={6} md={clients.length > 1 ? 2.5 : 4}>
            <TextField fullWidth size="small" label="Montant reçu (F TTC)" placeholder="ex. 45 632 000" value={f.montant_recu}
              onChange={(e) => setF({ ...f, montant_recu: e.target.value })} inputProps={{ inputMode: 'numeric' }}
              InputProps={{ endAdornment: recu > 0 ? (
                <InputAdornment position="end">
                  <Button size="small" startIcon={<AutoFixHighOutlined />} onClick={repartir} sx={{ whiteSpace: 'nowrap' }}>Répartir</Button>
                </InputAdornment>
              ) : null }}
              helperText={recu > 0 ? '« Répartir » coche les factures les plus anciennes jusqu\'à ce montant' : 'Facultatif : sert à contrôler l\'écart'} />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 600, flex: 1 }}>Factures réglées par ce paiement</Typography>
          <TextField size="small" placeholder="N° de facture ou de BC" value={q} onChange={(e) => setQ(e.target.value)} sx={{ width: 240 }}
            inputProps={{ 'aria-label': 'Filtrer les factures' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> }} />
        </Box>
        <TableContainer sx={{ border: `1px solid ${tokens.line}`, borderRadius: 1, maxHeight: 380 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Facture</TableCell><TableCell>BC</TableCell><TableCell>Échéance</TableCell>
                <TableCell align="right">Reste à payer TTC</TableCell><TableCell align="right" sx={{ width: 170 }}>Montant réglé TTC</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibles.map((x) => {
                const on = sel[cle(x)] !== undefined;
                const m = montantDe(x);
                const bad = on && x.source === 'ERP' && (!(m > 0) || m > x.reste_ttc);
                return (
                  <TableRow key={cle(x)} hover selected={on}>
                    <TableCell padding="checkbox"><Checkbox checked={on} onChange={() => basculer(x)} inputProps={{ 'aria-label': `Régler ${x.numero}` }} /></TableCell>
                    <TableCell sx={{ fontFamily: tokens.mono, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={() => basculer(x)}>
                      {x.numero}
                      {x.source === 'REPRISE' && <Chip size="small" label="reprise" variant="outlined" sx={{ ml: 0.75, height: 18, fontSize: 10.5 }} />}
                      {x.type === 'AVOIR' && <Chip size="small" label="avoir" color="warning" variant="outlined" sx={{ ml: 0.75, height: 18, fontSize: 10.5 }} />}
                      {x.type === 'ECART' && <Chip size="small" label="écart" color="warning" variant="outlined" sx={{ ml: 0.75, height: 18, fontSize: 10.5 }} />}
                    </TableCell>
                    <TableCell sx={{ fontFamily: tokens.mono, fontSize: 13, whiteSpace: 'nowrap' }}>{x.marche_rang ? `n°${x.marche_rang} · ` : ''}{x.marche_numero}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {dateFr(x.echeance) || '—'}{x.echu && <Chip size="small" label="échu" color="error" sx={{ ml: 0.75, height: 18, fontSize: 10.5 }} />}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{fcfa(x.reste_ttc)}</TableCell>
                    <TableCell align="right">
                      {on && x.source === 'ERP' ? (
                        <TextField size="small" variant="standard" value={sel[cle(x)]} error={bad}
                          onChange={(e) => setSel((s) => ({ ...s, [cle(x)]: e.target.value }))}
                          inputProps={{ inputMode: 'numeric', style: { textAlign: 'right', fontFamily: tokens.mono }, 'aria-label': `Montant réglé ${x.numero}` }}
                          helperText={bad ? `max ${fcfa(x.reste_ttc)}` : (m < x.reste_ttc ? 'partiel' : '')} />
                      ) : on ? <Box component="span" sx={{ fontFamily: tokens.mono }}>{fcfa(x.reste_ttc)}</Box> : ''}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!visibles.length && <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucune facture à encaisser{q ? ' pour cette recherche' : ''}.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.75 }}>
          Une facture de l'ERP peut être réglée en partie (le reste demeure à encaisser). Une facture reprise des fichiers est soldée en totalité.
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'flex-end', mt: 2, p: 1.5, bgcolor: '#F7F8FA', borderRadius: 1.5 }}>
          <Box><Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Factures cochées</Typography><Typography sx={{ fontWeight: 700 }}>{coches.length}</Typography></Box>
          <Box><Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Total réglé (TTC)</Typography><Typography sx={{ fontWeight: 700, fontFamily: tokens.mono }}>{fcfa(affecte)} F</Typography></Box>
          {recu !== null && Number.isFinite(recu) && (
            <>
              <Box><Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Montant reçu</Typography><Typography sx={{ fontWeight: 700, fontFamily: tokens.mono }}>{fcfa(recu)} F</Typography></Box>
              <Box>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Écart</Typography>
                <Typography sx={{ fontWeight: 700, fontFamily: tokens.mono, color: ecart === 0 ? '#1B7A46' : '#B7791F' }}>{ecart === 0 ? '0 ✓' : `${fcfa(ecart)} F`}</Typography>
              </Box>
            </>
          )}
        </Box>
        {ecart !== null && ecart !== 0 && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {ecart > 0 ? `Il reste ${fcfa(ecart)} F reçus non affectés à une facture.` : `Les factures cochées dépassent le montant reçu de ${fcfa(-ecart)} F.`}
            {' '}Vérifiez l'avis de paiement (retenue, frais bancaires, facture manquante…). L'écart restera noté sur le règlement.
          </Alert>
        )}
        <TextField fullWidth size="small" multiline minRows={1} label="Note (facultatif)" sx={{ mt: 2 }} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" color="success" disabled={busy || !coches.length || invalides.length > 0 || !f.date_reglement} onClick={enregistrer}>
          Enregistrer le règlement{coches.length ? ` (${coches.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Historique des règlements reçus, avec annulation d'un règlement saisi par erreur. */
export function Reglements({ ecriture, onChange, navigate }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const load = () => marcheAPI.reglements().then((r) => setRows(r.data.reglements)).catch((e) => setError(apiError(e)));
  useEffect(() => { load(); }, []);
  const annuler = async (r) => {
    if (!window.confirm(`Annuler le règlement du ${dateFr(r.date_reglement)}${r.reference ? ` (${r.reference})` : ''} ? Les ${r.factures.length} facture(s) redeviendront à encaisser.`)) return;
    try { await marcheAPI.annulerReglement(r.id); await load(); onChange?.(); } catch (e) { setError(apiError(e)); }
  };
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      {error && <Alert severity="error" sx={{ m: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell><TableCell>Référence</TableCell><TableCell>Mode</TableCell><TableCell>Factures réglées</TableCell>
            <TableCell align="right">Réglé TTC</TableCell><TableCell align="right">Reçu</TableCell><TableCell align="right">Écart</TableCell><TableCell>Saisi par</TableCell>
            {ecriture && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.map((r) => (
            <TableRow key={r.id}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateFr(r.date_reglement)}</TableCell>
              <TableCell sx={{ fontFamily: tokens.mono, fontSize: 13, whiteSpace: 'nowrap' }}>{r.reference || '—'}{r.note && <Typography sx={{ fontSize: 12, color: 'text.secondary', fontFamily: tokens.sans }}>{r.note}</Typography>}</TableCell>
              <TableCell>{PAYMENT_MODES[r.mode] || r.mode}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {r.factures.map((x) => (
                    <Chip key={`${x.source}-${x.id}`} size="small" variant="outlined" label={`${x.numero} · ${fcfa(x.montant)}`}
                      onClick={() => navigate(x.source === 'ERP' ? `/factures/${x.id}` : `/marches/${x.marche_id}`)} sx={{ fontFamily: tokens.mono, fontSize: 11.5 }} />
                  ))}
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600, whiteSpace: 'nowrap' }}>{fcfa(r.montant_affecte)}</TableCell>
              <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{r.montant_recu === null ? '—' : fcfa(r.montant_recu)}</TableCell>
              <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap', color: r.ecart ? '#B7791F' : tokens.steel }}>{r.ecart === null ? '—' : fcfa(r.ecart)}</TableCell>
              <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{r.saisi_par || '—'}</TableCell>
              {ecriture && <TableCell><Button size="small" color="error" onClick={() => annuler(r)}>Annuler</Button></TableCell>}
            </TableRow>
          ))}
          {rows && !rows.length && <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucun règlement enregistré.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
